import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BarChart } from "@outpave/ds";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { formatCash, formatCashFull } from "../../../utils/formatCash";
import { Center } from "components/layout/reusableLayouts";
import useElementSize from "hooks/useElementSize";
import type { SpendingMonth } from "hooks/useSpendingData";

interface TopSpendingProps {
  months: SpendingMonth[];
  /** Series in stacking order, which is also the legend's order. */
  keys: string[];
  labels: Record<string, string>;
  colors: Record<string, string>;
  currency: string;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  highlightedId: string | null;
  onHighlightChange: (id: string | null) => void;
}

export const PLOT_HEIGHT = 320;
/** Room one month label needs on the axis, in px. */
const LABEL_WIDTH = 64;

/** Every nth month gets a label, so labels never crowd a narrow axis. */
export const labelStride = (count: number, width: number) => {
  if (width <= 0) return 1;
  const fits = Math.max(1, Math.floor(width / LABEL_WIDTH));
  return Math.max(1, Math.ceil(count / fits));
};

/** The month a bucket covers, in the reader's locale. */
export const monthLabel = (
  bucket: Pick<SpendingMonth, "name" | "monthKey">,
  format: Intl.DateTimeFormat,
) => {
  const first = new Date(`${bucket.monthKey}-01T00:00:00Z`);
  return Number.isNaN(first.getTime()) ? bucket.name : format.format(first);
};

/** Spend by month, one stacked series per group. */
const TopSpending: React.FC<TopSpendingProps> = ({
  months,
  keys,
  labels,
  colors,
  currency,
  selectedIds,
  onSelectionChange,
  highlightedId,
  onHighlightChange,
}) => {
  const { t, i18n } = useTranslation();
  const [plotRef, plot] = useElementSize<HTMLDivElement>();

  const monthFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        month: "short",
        year: "2-digit",
        timeZone: "UTC",
      }),
    [i18n.language],
  );

  if (months.length === 0) {
    return (
      <Center sx={{ width: "100%", height: PLOT_HEIGHT }}>
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t("No Data Found")}
        </Typography>
      </Center>
    );
  }

  const series = keys.map((key) => ({
    id: key,
    label: labels[key] ?? key,
    color: colors[key],
    stack: "spend",
    data: months.map((month) =>
      typeof month[key] === "number" ? (month[key] as number) : 0,
    ),
    // A group with no spend that month has nothing to say in the tooltip.
    valueFormatter: (value: number) =>
      value === 0 ? null : formatCashFull(value, currency),
  }));

  const stride = labelStride(months.length, plot.width);

  return (
    <Box
      ref={plotRef}
      sx={{ position: "relative", height: PLOT_HEIGHT, minWidth: 0 }}
    >
      <Box sx={{ position: "absolute", inset: 0 }}>
        <BarChart
          fill
          legend={false}
          grid={{ horizontal: true }}
          // The package runs the plot to the card edge, which clips the last label.
          margin={{ right: 20 }}
          series={series}
          // A band axis is not reached by the axis valueFormatter, so the
          // months are formatted into the band values.
          xAxis={[
            {
              data: months.map((month) => monthLabel(month, monthFormat)),
              scaleType: "band",
              tickInterval: (_value, index) => index % stride === 0,
            },
          ]}
          yAxis={[
            {
              min: 0,
              valueFormatter: (value) => formatCash(value, currency) ?? "",
            },
          ]}
          selectedIds={selectedIds}
          onSelectionChange={onSelectionChange}
          highlightedId={highlightedId}
          onHighlightChange={onHighlightChange}
        />
      </Box>
    </Box>
  );
};

export default TopSpending;
